import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";

//helpers:
import {dateFormatter} from "../../../../_metronic/_helpers";

//components:
import {ConfirmDelete, EnhancedTableHead, PaginationTable} from "../../../widgets";
import UploadSocketManager from "./uploadSocketManager";
import {
    Button,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableRow,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ClearRoundedIcon from "@material-ui/icons/ClearRounded";
import {CustomTooltip} from "../../../widgets/customTooltip/index";
import RetryProcessDropdown from "./RetryProcessDropdown";
import {convertToIng, convertToParticiple, fromSnakeOrKebabCaseToNatural} from "../../../util/stringformatting";

//styles:
const videoListStyles = makeStyles(theme => ({
    root: {
        width: "100%",
    },
    table: {
        minWidth: 500,
    },
    tableWrapper: {
        overflowX: "auto",
    },
}));

const VideosUploadingTable = ({t}) => {
    const classes = videoListStyles();

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("title");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const socketUrl = process.env.REACT_APP_UPLOAD_URL;
    const company_id = useSelector(state => state.context?.company_id);
    const [tableData, setTableData] = useState([]);
    const [tableError, setTableError] = useState({});
    const [tableLoading, setTableLoading] = useState(true);
    const [socketManager, setSocketManager] = useState();

    const headRows = [
        {
            id: "title",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.TITLE"),
        },
        {
            id: "track",
            align: "left",
            disablePadding: false,
            label: t("GENERAL.TRACK"),
        },
        {
            id: "created_at",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.UPLOADED_AT"),
        },
        {
            id: "status",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.STATUS"),
        },
        {
            id: "actions",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.ACTIONS"),
        },
    ];

    const desc = (a, b, orderBy) => {
        const aLowerCase = (a[orderBy] || "").toString().toLowerCase();
        const bLowerCase = (b[orderBy] || "").toString().toLowerCase();

        if (bLowerCase < aLowerCase) {
            return -1;
        }
        if (bLowerCase > aLowerCase) {
            return 1;
        }
        return 0;
    };
    const stableSort = (array, cmp) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        const stableSorted = stabilizedThis.map(el => el[0]);
        return stableSorted;
    };
    const getSorting = (order, orderBy) => {
        return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
    };
    const handleRequestSort = (event, property) => {
        const isDesc = orderBy === property && order === "desc";
        setOrder(isDesc ? "asc" : "desc");
        setOrderBy(property);
    };

    //TODO Potser utilitzar: const uppy = React.useMemo(() => {
    useEffect(() => {
        if (!socketManager) {
            const originalObject = new UploadSocketManager(socketUrl, {
                reconnectionAttempts: 3,
            });

            setSocketManager(originalObject);

            originalObject.getTableData(company_id, setTableData, setTableError, setTableLoading);

            return () => {
                if (originalObject) {
                    originalObject.disconnect();
                }
            };
        }
    }, []);

    const cancelProcess = processId => {
        socketManager.cancelProcess(company_id, processId);
    };
    const removeProcess = processId => {
        socketManager.removeProcess(company_id, processId);
    };
    const restartProcess = processId => {
        socketManager.restartProcess(company_id, processId);
    };
    const retryProcess = processId => {
        socketManager.retryProcess(company_id, processId);
    };

    const format_current_step_message = step_name => {
        try {
            let message = step_name;
            try {
                if (step_name?.length) {
                    message = fromSnakeOrKebabCaseToNatural(step_name);
                    message = convertToIng(message);
                    message = message + "...";
                }
            } catch (error) {
                if (typeof step_name === typeof String() && step_name?.length) {
                    message = "Current step: " + step_name;
                }
                console.error(error);
            }
            return message;
        } catch (error) {
            console.error(error);
        }
        return null;
    };

    const format_last_step_message = step_name => {
        try {
            let message = step_name;
            try {
                if (step_name?.length) {
                    message = fromSnakeOrKebabCaseToNatural(step_name);
                    message = convertToParticiple(message);
                    message = message + ".";
                }
            } catch (error) {
                if (typeof step_name === typeof String() && step_name?.length) {
                    message = "Last successful step: " + step_name;
                }
                console.error(error);
            }
            return message;
        } catch (error) {
            console.error(error);
        }
        return null;
    };

    return (
        <div className="w-100 position-relative">
            <div className="card card-custom gutter-b shadow">
                <div className="card-header flex-wrap pt-6 pb-0 border-bottom border-gray">
                    <div className="card-title">{t("ADMINPANEL.UPLOAD.TABLE.TITLE")}</div>
                </div>
                <div className="card-body">
                    <div className={`${classes.root}`}>
                        {!tableLoading ? (
                            tableData?.length ? (
                                <div className={`custom-scroll-area horizontal-scroll`}>
                                    <Table className={classes.table}>
                                        <EnhancedTableHead
                                            order={order}
                                            orderBy={orderBy}
                                            onRequestSort={handleRequestSort}
                                            rowCount={tableData?.length}
                                            headRows={headRows}
                                        />
                                        <TableBody>
                                            {tableData &&
                                                stableSort(tableData, getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map(
                                                        (
                                                            {
                                                                id = 0,
                                                                thumbnail = "",
                                                                title = "",
                                                                track = "",
                                                                created_at = "",
                                                                percentage = "",
                                                                status = {id: 0, value: "", type: ""},
                                                                cancelling = false,
                                                                type_id = 0,
                                                                error_message = null,
                                                                current_step = null,
                                                                last_step = null,
                                                                message = error_message ||
                                                                    format_current_step_message(current_step) ||
                                                                    format_last_step_message(last_step) ||
                                                                    null,
                                                            },
                                                            i
                                                        ) => (
                                                            <TableRow key={i}>
                                                                <TableCell align="left" component="th" scope="row">
                                                                    <div
                                                                        className="d-flex align-items-center"
                                                                        style={{width: "300px"}}
                                                                    >
                                                                        <div className="d-flex position-relative w-50px h-50px">
                                                                            <span
                                                                                className="bg_image_cover rounded h-100 w-100"
                                                                                style={{
                                                                                    backgroundImage: `url(${thumbnail ||
                                                                                        (type_id === 1
                                                                                            ? "/media/dummy/video_dummy.png"
                                                                                            : "/media/dummy/document_dummy.png")})`,
                                                                                }}
                                                                            />
                                                                        </div>

                                                                        {/* title: */}
                                                                        <div
                                                                            className="ml-4 position-relative"
                                                                            style={{
                                                                                width: "220px",
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className="text-dark-75 font-size-base mb-0 elipsis_3lines"
                                                                                style={{
                                                                                    maxHeight: "52px",
                                                                                }}
                                                                            >
                                                                                {title}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </TableCell>

                                                                {/* trackName: */}
                                                                <TableCell
                                                                    align="left"
                                                                    className="mb-0 font-size-1rem w-200px"
                                                                >
                                                                    {track}
                                                                </TableCell>

                                                                {/* created at: */}
                                                                <TableCell
                                                                    align="left"
                                                                    className="text-primary mb-0 table_formatted_date"
                                                                    style={{width: "140px"}}
                                                                >
                                                                    {created_at && dateFormatter(created_at)}
                                                                </TableCell>

                                                                {/* status: */}
                                                                <TableCell
                                                                    align="left"
                                                                    className="mb-0 font-size-1rem"
                                                                    style={{width: "140px"}}
                                                                >
                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <CustomTooltip
                                                                            config={{
                                                                                text:
                                                                                    message
                                                                                        ?.replaceAll("<", "")
                                                                                        ?.replaceAll(">", "") || null,
                                                                                placement: message?.length
                                                                                    ? "top"
                                                                                    : "none",
                                                                            }}
                                                                        >
                                                                            <Button
                                                                                className={`btn btn-${(status &&
                                                                                    status.type) ||
                                                                                    "light"} w-100`}
                                                                                // disabled
                                                                            >
                                                                                <span className="d-flex flex-column">
                                                                                    {(status && status.value) || ""}
                                                                                    {percentage &&
                                                                                    typeof percentage === "number" ? (
                                                                                        <small>{percentage} %</small>
                                                                                    ) : null}
                                                                                </span>
                                                                            </Button>
                                                                        </CustomTooltip>
                                                                    </div>
                                                                </TableCell>

                                                                {/* actions: */}
                                                                <TableCell align="left" className="w-150px">
                                                                    <div className="d-flex align-items-center justify-content-betwee">
                                                                        {status.id === 0 || status.id === 1 ? (
                                                                            <Button
                                                                                className="btn btn-light"
                                                                                onClick={() => {
                                                                                    if (!cancelling) cancelProcess(id);
                                                                                }}
                                                                            >
                                                                                <span className="w-70px h-20px">
                                                                                    {cancelling ? (
                                                                                        <CircularProgress
                                                                                            className="h-20px w-20px"
                                                                                            color="white"
                                                                                        />
                                                                                    ) : (
                                                                                        t(
                                                                                            "ADMINPANEL.UPLOAD.TABLE.CANCEL"
                                                                                        )
                                                                                    )}
                                                                                </span>
                                                                            </Button>
                                                                        ) : status.id === -1 || status.id === 3 ? (
                                                                            <div className="d-flex pt-6 pb-6">
                                                                                <CustomTooltip
                                                                                    config={{
                                                                                        text: t(
                                                                                            "ADMINPANEL.UPLOAD.TABLE.REMOVE"
                                                                                        ),
                                                                                        placement: error_message
                                                                                            ? "top"
                                                                                            : "none",
                                                                                    }}
                                                                                >
                                                                                    <span
                                                                                        className="btn btn-light mr-3 d-flex"
                                                                                        onClick={() =>
                                                                                            removeProcess(id)
                                                                                        }
                                                                                    >
                                                                                        <ClearRoundedIcon></ClearRoundedIcon>
                                                                                    </span>
                                                                                </CustomTooltip>
                                                                                <RetryProcessDropdown
                                                                                    handleRetryAll={() =>
                                                                                        restartProcess(id)
                                                                                    }
                                                                                    handleRetryFailed={() =>
                                                                                        retryProcess(id)
                                                                                    }
                                                                                ></RetryProcessDropdown>
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}

                                            <TableRow style={{height: 48}}>
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        </TableBody>

                                        {/* pagination: */}
                                        <TableFooter>
                                            <TableRow>
                                                <PaginationTable
                                                    setPage={setPage}
                                                    setRowsPerPage={setRowsPerPage}
                                                    rows={tableData}
                                                    rowsPerPage={rowsPerPage}
                                                    page={page}
                                                    rowsPerPageOptions={[5, 10, 25]}
                                                    variant="filled"
                                                />
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </div>
                            ) : (
                                t("ADMINPANEL.UPLOAD.TABLE.NO_PROCESSES")
                            )
                        ) : (
                            <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100">
                                <CircularProgress />
                            </div>
                        )}
                        {tableError?.text ? (
                            <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100">
                                <div className={`alert alert-${tableError?.type || "info"} font-size-h5`}>
                                    <WarningRoundedIcon color="inherit" className="mr-5" />
                                    {tableError?.text}
                                </div>
                            </div>
                        ) : null}

                        {/* confirm delete: */}
                        <ConfirmDelete
                            show={showDeleteModal}
                            onConfirm={() => setShowDeleteModal(false)}
                            onCancel={() => {
                                setShowDeleteModal(false);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideosUploadingTable;
